import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertBlogAccess, assertBlogAdmin } from "@/lib/blog-guard.server";
import { logAdminAction } from "@/lib/audit.server";
import { aiImage, aiText, mapPost, publicClient, storeCoverImage } from "@/lib/blog.server";
import type { Json } from "@/integrations/supabase/types";
import { slugify } from "@/lib/blog";
import { cached, invalidateCached } from "@/lib/server-cache";
import { findGaps } from "@/lib/content-gaps";
import { schoolFactsBlock } from "@/lib/school-facts.server";
import {
  SEO_DESC_MAX,
  SEO_DESC_MIN,
  SEO_RULES_PROMPT,
  SEO_TITLE_MAX,
  normalizeSeoDescription,
  normalizeSeoTitle,
} from "@/lib/seo-fields";

const localeSchema = z.enum(["sv", "en", "es"]);

/** Publik lista med publicerade inlägg. */
export const listPublishedPosts = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z
      .object({ locale: localeSchema, limit: z.number().int().min(1).max(50).default(24) })
      .parse(data),
  )
  .handler(async ({ data }) =>
    cached(`blog-list:${data.locale}:${data.limit}`, 60_000, async () => {
      const { data: rows } = await publicClient()
        .from("blog_posts")
        .select("*")
        .eq("locale", data.locale)
        .in("status", ["published", "scheduled"])
        .is("deleted_at", null)
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false })
        .limit(data.limit);
      return (rows ?? []).map(mapPost);
    }),
  );

/** Publikt enskilt inlägg. */
export const getPublishedPost = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => z.object({ slug: z.string().max(120) }).parse(data))
  .handler(async ({ data }) => cached(`blog-post:${data.slug}`, 5 * 60_000, async () => {
    const { data: row } = await publicClient()
      .from("blog_posts")
      .select("*")
      .eq("slug", data.slug)
      .in("status", ["published", "scheduled"])
      .is("deleted_at", null)
      .lte("published_at", new Date().toISOString())
      .maybeSingle();
    return row ? mapPost(row) : null;
  }));

/** Admin/redaktör: alla inlägg, grupperade per språkgrupp. */
export const listBlogPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { isAdmin } = await assertBlogAccess(context as never);
    const { data: rows } = await context.supabase
      .from("blog_posts")
      .select("*")
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });

    const list = rows ?? [];
    const posts = list.map(mapPost);

    // Slå upp e-post för den som sparade senast (visas i listan).
    const editorIds = Array.from(
      new Set(list.map((r) => (r as { updated_by?: string | null }).updated_by).filter(Boolean)),
    ) as string[];
    const emails = new Map<string, string>();
    if (editorIds.length > 0) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: users } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 1000 });
        for (const u of users?.users ?? []) if (u.email) emails.set(u.id, u.email);
      } catch {
        /* utan admin-klient visas bara datum */
      }
    }

    type Version = {
      id: string;
      locale: string;
      slug: string;
      title: string;
      status: string;
      publishedAt: string | null;
      updatedAt: string;
      updatedByEmail: string | null;
    };
    const groups = new Map<string, { key: string; versions: Version[] }>();
    list.forEach((row, index) => {
      const post = posts[index]!;
      const key = (row as { translation_group_id?: string | null }).translation_group_id ?? `single:${post.id}`;
      const editorId = (row as { updated_by?: string | null }).updated_by ?? null;
      const version: Version = {
        id: post.id,
        locale: post.locale,
        slug: post.slug,
        title: post.title,
        status: post.status,
        publishedAt: post.publishedAt,
        updatedAt: post.updatedAt,
        updatedByEmail: editorId ? (emails.get(editorId) ?? null) : null,
      };
      const existing = groups.get(key);
      if (existing) existing.versions.push(version);
      else groups.set(key, { key, versions: [version] });
    });

    const order = ["sv", "en", "es"];
    const grouped = Array.from(groups.values()).map((group) => {
      const versions = [...group.versions].sort(
        (a, b) => order.indexOf(a.locale) - order.indexOf(b.locale),
      );
      const primary = versions.find((v) => v.locale === "sv") ?? versions[0]!;
      const published = versions
        .map((v) => v.publishedAt)
        .filter((v): v is string => Boolean(v))
        .sort();
      const latest = versions.reduce((a, b) => (a.updatedAt >= b.updatedAt ? a : b));
      const statuses = Array.from(new Set(versions.map((v) => v.status)));
      const authorName =
        posts.find((p) => p.id === primary.id)?.authorName ??
        posts.find((p) => p.id === versions[0]!.id)?.authorName ??
        "";
      return {
        key: group.key,
        primaryId: primary.id,
        title: primary.title,
        slug: primary.slug,
        authorName,
        locales: versions.map((v) => v.locale),
        versions,
        status: statuses.length === 1 ? statuses[0]! : "mixed",
        statuses,
        publishedAt: published[0] ?? null,
        updatedAt: latest.updatedAt,
        updatedByEmail: latest.updatedByEmail,
      };
    });

    return { posts, groups: grouped, isAdmin };
  });


/** Admin/redaktör: ett inlägg med versionshistorik. */
export const getBlogPost = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { isAdmin } = await assertBlogAccess(context as never);
    const [{ data: row }, { data: revisions }] = await Promise.all([
      context.supabase.from("blog_posts").select("*").eq("id", data.id).maybeSingle(),
      context.supabase
        .from("blog_post_revisions")
        .select("id, note, created_at, created_by_email, snapshot")
        .eq("post_id", data.id)
        .order("created_at", { ascending: false })
        .limit(30),
    ]);
    if (!row) throw new Error("Inlägget hittades inte.");
    return { post: mapPost(row), revisions: revisions ?? [], isAdmin };
  });

const postInput = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().max(120).default(""),
  locale: localeSchema.default("sv"),
  title: z.string().max(200).default(""),
  excerpt: z.string().max(600).default(""),
  content: z.string().max(60000).default(""),
  sections: z.array(z.record(z.unknown())).max(60).default([]),
  coverImageUrl: z.string().max(500).default(""),
  coverImageAlt: z.string().max(300).default(""),
  coverFocalX: z.number().min(0).max(100).default(50),
  coverFocalY: z.number().min(0).max(100).default(50),
  coverAspect: z.string().max(20).default(""),
  coverFit: z.enum(["cover", "contain"]).default("cover"),
  category: z.string().max(80).default(""),
  tags: z.array(z.string().max(40)).max(12).default([]),
  authorName: z.string().max(120).default(""),
  seoTitle: z.string().max(200).default(""),
  seoDescription: z.string().max(400).default(""),
  status: z.enum(["draft", "scheduled", "published"]).default("draft"),
  publishedAt: z.string().nullable().default(null),
});

/** Admin/redaktör: skapa eller uppdatera. Sparar alltid en version av det gamla innehållet. */
export const saveBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => postInput.parse(data))
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const email = (context.claims as { email?: string } | undefined)?.email ?? "";

    const baseSlug = slugify(data.slug || data.title) || `inlagg-${Date.now()}`;
    const values = {
      slug: baseSlug,
      locale: data.locale,
      title: data.title,
      excerpt: data.excerpt,
      content: data.content,
      sections: data.sections as unknown as Json,
      cover_image_url: data.coverImageUrl,
      cover_image_alt: data.coverImageAlt,
      cover_focal_x: Math.round(data.coverFocalX),
      cover_focal_y: Math.round(data.coverFocalY),
      cover_aspect: data.coverAspect,
      cover_fit: data.coverFit,
      category: data.category,
      tags: data.tags,
      author_name: data.authorName,
      seo_title: data.seoTitle,
      seo_description: data.seoDescription,
      status: data.status,
      published_at: data.status === "draft" ? null : (data.publishedAt ?? new Date().toISOString()),
      updated_by: context.userId,
    };

    if (data.id) {
      const { data: before } = await context.supabase
        .from("blog_posts")
        .select("*")
        .eq("id", data.id)
        .maybeSingle();

      const { error } = await context.supabase.from("blog_posts").update(values).eq("id", data.id);
      if (error) throw new Error("Inlägget kunde inte sparas.");

      if (before) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin.from("blog_post_revisions").insert({
          post_id: data.id,
          snapshot: before,
          note: "Version före ändring",
          created_by: context.userId,
          created_by_email: email,
        });
      }

      await logAdminAction(context as never, {
        action: "blog.update",
        entity: "blog_post",
        entityId: data.id,
        summary: `Uppdaterade blogginlägget "${data.title}" (${data.status})`,
        meta: { status: data.status, slug: baseSlug },
      });
      const { logEditorialActivity } = await import("@/lib/editorial.server");
      await logEditorialActivity(context as never, {
        entity: "blog",
        entityId: data.id,
        action: before && before.status !== data.status ? "status" : "save",
        summary:
          before && before.status !== data.status
            ? `Status ändrad till ${data.status}`
            : `Sparade ändringar i "${data.title}"`,
        meta: { status: data.status },
      });
      invalidateCached("blog-");
      return { ok: true as const, id: data.id };
    }

    const { data: created, error } = await context.supabase
      .from("blog_posts")
      .insert({ ...values, created_by: context.userId })
      .select("id")
      .single();
    if (error || !created) throw new Error("Inlägget kunde inte skapas.");

    await logAdminAction(context as never, {
      action: "blog.create",
      entity: "blog_post",
      entityId: created.id,
      summary: `Skapade blogginlägget "${data.title}"`,
      meta: { status: data.status, slug: baseSlug },
    });
    const { logEditorialActivity } = await import("@/lib/editorial.server");
    await logEditorialActivity(context as never, {
      entity: "blog",
      entityId: created.id,
      action: "create",
      summary: `Skapade inlägget "${data.title}"`,
      meta: { status: data.status },
    });
    invalidateCached("blog-");
    return { ok: true as const, id: created.id };
  });

/** Admin: ta bort ett inlägg. */
export const deleteBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertBlogAdmin(context as never);
    const { softDeleteRow } = await import("@/lib/trash.server");
    await softDeleteRow(context as never, "blog_posts", data.id);
    await logAdminAction(context as never, {
      action: "blog.delete",
      entity: "blog_post",
      entityId: data.id,
      severity: "warning",
      summary: "Tog bort ett blogginlägg",
    });
    invalidateCached("blog-");
    return { ok: true as const };
  });

/** Admin: återställ en tidigare version. */
export const restoreBlogRevision = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ postId: z.string().uuid(), revisionId: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAdmin(context as never);
    const email = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [{ data: revision }, { data: before }] = await Promise.all([
      supabaseAdmin
        .from("blog_post_revisions")
        .select("snapshot")
        .eq("id", data.revisionId)
        .maybeSingle(),
      supabaseAdmin.from("blog_posts").select("*").eq("id", data.postId).maybeSingle(),
    ]);
    const snapshot = revision?.snapshot as Record<string, unknown> | undefined;
    if (!snapshot) throw new Error("Versionen hittades inte.");

    if (before) {
      await supabaseAdmin.from("blog_post_revisions").insert({
        post_id: data.postId,
        snapshot: before,
        note: "Version före återställning",
        created_by: context.userId,
        created_by_email: email,
      });
    }

    const { id: _id, created_at: _c, updated_at: _u, ...rest } = snapshot as Record<string, never>;
    const { error } = await supabaseAdmin
      .from("blog_posts")
      .update({ ...rest, updated_by: context.userId })
      .eq("id", data.postId);
    if (error) throw new Error("Versionen kunde inte återställas.");

    await logAdminAction(context as never, {
      action: "blog.restore_revision",
      entity: "blog_post",
      entityId: data.postId,
      severity: "warning",
      summary: "Återställde en tidigare version av ett blogginlägg",
      meta: { revisionId: data.revisionId },
    });
    invalidateCached("blog-");
    return { ok: true as const };
  });

/** AI-hjälp: skriver utkast till text, ingress och SEO. */
export const draftBlogPostWithAi = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        topic: z.string().trim().min(3).max(400),
        locale: localeSchema.default("sv"),
        tone: z.string().max(120).default(""),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const system =
      data.locale === "sv"
        ? "Du skriver blogginlägg för Swedish International School, en svensk skola i Estepona/Marbella i Spanien. Skriv sakligt, varmt och konkret på svenska. Lova aldrig mer än en skola rimligen kan hålla, hitta aldrig på fakta, siffror eller citat. Svara ENDAST med giltig JSON."
        : "You write blog posts for Swedish International School, a Swedish school in Estepona/Marbella, Spain. Write factual, warm and concrete English. Never overpromise or invent facts, figures or quotes. Reply ONLY with valid JSON.";
    const user = `${data.tone ? `Ton: ${data.tone}. ` : ""}Ämne: ${data.topic}

${await schoolFactsBlock()}

${SEO_RULES_PROMPT}

    Svara med JSON: {"title": string, "excerpt": string (max 200 tecken), "content": string (600-800 ord i markdown med ## rubriker och punktlistor), "sections": [{"type": "heading|text|image|imageText|textOverImage|button|divider", "heading": string, "text": string, "imagePrompt": string, "alt": string, "caption": string, "label": string, "url": string, "align": "left|right"}] (3-5 kompletterande block; minst hälften bildsektioner med engelsk imagePrompt och alt-text, aldrig src), "seoTitle": string (max ${SEO_TITLE_MAX} tecken), "seoDescription": string (${SEO_DESC_MIN}-${SEO_DESC_MAX} tecken), "tags": string[] (3-5), "category": string, "coverPrompt": string (engelsk bildprompt för omslagsbild)}`;

    const raw = await aiText(system, user);
    const json = raw.slice(raw.indexOf("{"), raw.lastIndexOf("}") + 1);
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(json) as Record<string, unknown>;
    } catch {
      throw new Error("AI-svaret kunde inte tolkas. Försök igen.");
    }

    await logAdminAction(context as never, {
      action: "blog.ai_draft",
      entity: "blog_post",
      summary: `Skapade AI-utkast om "${data.topic.slice(0, 80)}"`,
      meta: { locale: data.locale },
    });

    const str = (k: string) => (typeof parsed[k] === "string" ? (parsed[k] as string) : "");
    const { parseSections } = await import("@/lib/editorial");
    const sections = parseSections(parsed["sections"]).slice(0, 8);
    return {
      title: str("title"),
      excerpt: str("excerpt"),
      content: str("content"),
      sections,
      seoTitle: normalizeSeoTitle(str("seoTitle") || str("title")),
      seoDescription: normalizeSeoDescription(str("seoDescription"), str("excerpt")),
      category: str("category"),
      coverPrompt: str("coverPrompt"),
      tags: Array.isArray(parsed["tags"]) ? (parsed["tags"] as string[]).slice(0, 6) : [],
    };
  });

/** AI-hjälp: genererar och sparar en omslagsbild. */
export const generateBlogCover = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({ prompt: z.string().trim().min(3).max(600), slug: z.string().max(120).default("") })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const dataUrl = await aiImage(
      `Editorial cover photo for a Swedish school blog on the Costa del Sol. ${data.prompt}. Natural light, warm Mediterranean palette, documentary style, no text, no logos, no recognisable faces.`,
    );
    const url = await storeCoverImage(dataUrl, slugify(data.slug) || "inlagg");
    await logAdminAction(context as never, {
      action: "blog.ai_image",
      entity: "blog_post",
      summary: "Genererade en omslagsbild med AI",
      meta: { path: url },
    });
    return { url };
  });

/* --------------------- Slug, författare och översättning --------------------- */

/** Kontrollerar att en webbadress är ledig inom språket och föreslår ett ledigt alternativ. */
export const checkBlogSlug = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        slug: z.string().max(140).default(""),
        locale: localeSchema.default("sv"),
        id: z.string().uuid().nullable().default(null),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const base = slugify(data.slug);
    if (!base) return { slug: "", available: false, suggestion: "", reason: "empty" as const };

    const { data: rows } = await context.supabase
      .from("blog_posts")
      .select("id, slug")
      .eq("locale", data.locale)
      .is("deleted_at", null)
      .like("slug", `${base}%`);

    const taken = new Set((rows ?? []).filter((r) => r.id !== data.id).map((r) => r.slug));
    if (!taken.has(base))
      return { slug: base, available: true, suggestion: base, reason: "ok" as const };

    let n = 2;
    while (taken.has(`${base}-${n}`) && n < 100) n += 1;
    return { slug: base, available: false, suggestion: `${base}-${n}`, reason: "taken" as const };
  });

/** Tidigare använda författarnamn för autocomplete. */
export const listBlogAuthors = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertBlogAccess(context as never);
    const { data } = await context.supabase.from("blog_posts").select("author_name").limit(500);
    const counts = new Map<string, number>();
    for (const row of data ?? []) {
      const name = (row.author_name ?? "").trim();
      if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return {
      authors: [...counts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "sv"))
        .map(([name, count]) => ({ name, count })),
    };
  });

type TranslationVersion = {
  id: string;
  locale: string;
  title: string;
  status: string;
  slug: string;
  publishedAt: string | null;
};

/** Språkversioner av samma inlägg. Borttagna versioner listas separat. */
export const listPostTranslations = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const { data: row } = await context.supabase
      .from("blog_posts")
      .select("translation_group_id")
      .eq("id", data.id)
      .maybeSingle();
    const group = row?.translation_group_id;
    const empty = { versions: [] as TranslationVersion[], trashed: [] as TranslationVersion[] };
    if (!group) return empty;
    const { data: rows } = await context.supabase
      .from("blog_posts")
      .select("id, locale, title, status, slug, published_at, deleted_at")
      .eq("translation_group_id", group);
    type RawRow = {
      id: string;
      locale: string;
      title: string;
      status: string;
      slug: string;
      published_at: string | null;
      deleted_at: string | null;
    };
    const map = (r: RawRow): TranslationVersion => ({
      id: r.id,
      locale: r.locale,
      title: r.title,
      status: r.status,
      slug: r.slug,
      publishedAt: r.published_at,
    });
    return {
      versions: (rows ?? []).filter((r) => !r.deleted_at).map(map),
      trashed: (rows ?? []).filter((r) => !!r.deleted_at).map(map),
    };
  });

/** Publicerar eller avpublicerar alla språkversioner i samma språkgrupp. */
export const setBlogGroupStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({ id: z.string().uuid(), status: z.enum(["published", "draft"]) })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const { data: row } = await context.supabase
      .from("blog_posts")
      .select("translation_group_id")
      .eq("id", data.id)
      .maybeSingle();
    const group = row?.translation_group_id;
    if (!group) throw new Error("Inlägget saknar en kopplad språkgrupp.");

    const { data: rows } = await context.supabase
      .from("blog_posts")
      .select("id, locale, title, status, published_at")
      .eq("translation_group_id", group)
      .is("deleted_at", null);

    const email = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const changed: string[] = [];
    for (const r of rows ?? []) {
      if (r.status === data.status) continue;
      const { data: before } = await context.supabase
        .from("blog_posts")
        .select("*")
        .eq("id", r.id)
        .maybeSingle();
      const { error } = await context.supabase
        .from("blog_posts")
        .update({
          status: data.status,
          published_at:
            data.status === "draft" ? null : (r.published_at ?? new Date().toISOString()),
          updated_by: context.userId,
        })
        .eq("id", r.id);
      if (error) throw new Error("Statusen kunde inte ändras för alla språk.");
      if (before)
        await supabaseAdmin.from("blog_post_revisions").insert({
          post_id: r.id,
          snapshot: before,
          note: "Version före statusändring",
          created_by: context.userId,
          created_by_email: email,
        });
      changed.push(r.locale);
    }

    await logAdminAction(context as never, {
      action: data.status === "published" ? "blog.publish_all" : "blog.unpublish_all",
      entity: "blog_post",
      entityId: data.id,
      summary:
        data.status === "published"
          ? `Publicerade alla språk (${changed.join(", ") || "inga ändringar"})`
          : `Avpublicerade alla språk (${changed.join(", ") || "inga ändringar"})`,
      meta: { status: data.status, locales: changed },
    });
    invalidateCached("blog-");
    return { ok: true as const, changed };
  });

/** Återställer en språkversion från papperskorgen. */
export const restoreBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertBlogAdmin(context as never);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("blog_posts")
      .update({ deleted_at: null, deleted_by: null, deleted_by_email: "" })
      .eq("id", data.id);
    if (error) throw new Error("Inlägget kunde inte återställas.");
    await logAdminAction(context as never, {
      action: "blog.restore",
      entity: "blog_post",
      entityId: data.id,
      summary: "Återställde en språkversion från papperskorgen",
    });
    invalidateCached("blog-");
    return { ok: true as const };
  });

const aiTranslationFields = z.object({
  title: z.string().max(200).optional(),
  slug: z.string().max(120).optional(),
  excerpt: z.string().max(600).optional(),
  content: z.string().max(60000).optional(),
  sections: z.array(z.record(z.unknown())).max(60).optional(),
  coverImageAlt: z.string().max(300).optional(),
  category: z.string().max(80).optional(),
  tags: z.array(z.string().max(40)).max(12).optional(),
  seoTitle: z.string().max(200).optional(),
  seoDescription: z.string().max(400).optional(),
});

/** Applies assistant patches to linked language rows and records a revision for every changed row. */
export const applyBlogAiTranslations = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({ id: z.string().uuid(), locales: z.record(localeSchema, aiTranslationFields) })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const { data: source } = await context.supabase
      .from("blog_posts")
      .select("translation_group_id")
      .eq("id", data.id)
      .maybeSingle();
    if (!source?.translation_group_id) throw new Error("Inlägget saknar en kopplad språkgrupp.");
    const { data: rows } = await context.supabase
      .from("blog_posts")
      .select("*")
      .eq("translation_group_id", source.translation_group_id)
      .is("deleted_at", null);
    const email = (context.claims as { email?: string } | undefined)?.email ?? "";
    const updated: string[] = [];
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    for (const row of rows ?? []) {
      const patch = data.locales[row.locale as "sv" | "en" | "es"];
      if (!patch || Object.keys(patch).length === 0) continue;
      const values = {
        ...(patch.title !== undefined ? { title: patch.title } : {}),
        ...(patch.slug !== undefined ? { slug: slugify(patch.slug) } : {}),
        ...(patch.excerpt !== undefined ? { excerpt: patch.excerpt } : {}),
        ...(patch.content !== undefined ? { content: patch.content } : {}),
        ...(patch.sections !== undefined ? { sections: patch.sections as unknown as Json } : {}),
        ...(patch.coverImageAlt !== undefined ? { cover_image_alt: patch.coverImageAlt } : {}),
        ...(patch.category !== undefined ? { category: patch.category } : {}),
        ...(patch.tags !== undefined ? { tags: patch.tags } : {}),
        ...(patch.seoTitle !== undefined ? { seo_title: normalizeSeoTitle(patch.seoTitle) } : {}),
        ...(patch.seoDescription !== undefined
          ? {
              seo_description: normalizeSeoDescription(
                patch.seoDescription,
                patch.excerpt ?? row.excerpt,
              ),
            }
          : {}),
        updated_by: context.userId,
      };
      const { error } = await context.supabase.from("blog_posts").update(values).eq("id", row.id);
      if (error) throw new Error(`Språkversionen ${row.locale} kunde inte uppdateras.`);
      await supabaseAdmin.from("blog_post_revisions").insert({
        post_id: row.id,
        snapshot: row,
        note: "Version före flerspråkig AI-infogning",
        created_by: context.userId,
        created_by_email: email,
      });
      updated.push(row.locale);
    }
    if (updated.length === 0) throw new Error("Inga befintliga språkversioner kunde uppdateras.");
    await logAdminAction(context as never, {
      action: "blog.ai_multilingual_update",
      entity: "blog_post",
      entityId: data.id,
      summary: `Infogade AI-förslag i språk: ${updated.join(", ")}`,
      meta: { locales: updated },
    });
    invalidateCached("blog-");
    return { ok: true as const, updated };
  });

/** Översätter ett inlägg till valda språk och sparar dem som kopplade utkast. */
export const translateBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        targets: z
          .array(z.enum(["sv", "en", "es"]))
          .min(1)
          .default(["en"]),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const { data: source } = await context.supabase
      .from("blog_posts")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (!source) throw new Error("Inlägget hittades inte.");

    const group = source.translation_group_id ?? crypto.randomUUID();
    if (!source.translation_group_id) {
      await context.supabase
        .from("blog_posts")
        .update({ translation_group_id: group })
        .eq("id", source.id);
    }

    const LANG = { sv: "Swedish", en: "English", es: "Spanish (Spain)" } as const;
    const results: { locale: string; id: string; title: string }[] = [];

    for (const target of data.targets) {
      const system = `You translate editorial content for Swedish International School in Estepona, Spain, into ${LANG[target]}. Translate faithfully, keep the markdown structure, keep proper nouns, never invent facts. Reply ONLY with valid JSON.`;
      const user = `Translate this blog post into ${LANG[target]}. Keep any {{LUCKA: ...}} markers untranslated and in place.

JSON in:
${JSON.stringify({
  title: source.title,
  excerpt: source.excerpt,
  content: source.content,
  seoTitle: source.seo_title,
  seoDescription: source.seo_description,
  category: source.category,
  tags: source.tags ?? [],
  coverImageAlt: source.cover_image_alt,
}).slice(0, 40000)}

Reply with JSON: {"title": string, "excerpt": string, "content": string (markdown), "seoTitle": string (max 60 chars), "seoDescription": string (max 155 chars), "category": string, "tags": string[], "coverImageAlt": string, "slug": string (url friendly, lowercase, ${target})}`;

      const raw = await aiText(system, user);
      const json = raw.slice(raw.indexOf("{"), raw.lastIndexOf("}") + 1);
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(json) as Record<string, unknown>;
      } catch {
        throw new Error(`Översättningen till ${LANG[target]} kunde inte tolkas. Försök igen.`);
      }
      const str = (k: string) => (typeof parsed[k] === "string" ? (parsed[k] as string) : "");

      const { data: existingRows } = await context.supabase
        .from("blog_posts")
        .select("id, slug")
        .eq("locale", target);
      const existing = (existingRows ?? []).find((r) => r.id !== source.id);
      const { data: sameGroup } = await context.supabase
        .from("blog_posts")
        .select("id")
        .eq("translation_group_id", group)
        .eq("locale", target)
        .maybeSingle();

      const taken = new Set(
        (existingRows ?? []).filter((r) => r.id !== sameGroup?.id).map((r) => r.slug),
      );
      let slug = slugify(str("slug") || str("title")) || `${source.slug}-${target}`;
      if (taken.has(slug)) {
        let n = 2;
        while (taken.has(`${slug}-${n}`) && n < 100) n += 1;
        slug = `${slug}-${n}`;
      }
      void existing;

      const values = {
        slug,
        locale: target,
        title: str("title"),
        excerpt: str("excerpt"),
        content: str("content"),
        cover_image_url: source.cover_image_url,
        cover_image_alt: str("coverImageAlt") || source.cover_image_alt,
        category: str("category") || source.category,
        tags: Array.isArray(parsed["tags"])
          ? (parsed["tags"] as string[]).slice(0, 8)
          : (source.tags ?? []),
        author_name: source.author_name,
        seo_title: str("seoTitle"),
        seo_description: str("seoDescription"),
        status: "draft",
        published_at: null,
        translation_group_id: group,
        updated_by: context.userId,
      };

      if (sameGroup?.id) {
        const { error } = await context.supabase
          .from("blog_posts")
          .update(values)
          .eq("id", sameGroup.id);
        if (error) throw new Error("Översättningen kunde inte sparas.");
        results.push({ locale: target, id: sameGroup.id, title: values.title });
      } else {
        const { data: created, error } = await context.supabase
          .from("blog_posts")
          .insert({ ...values, created_by: context.userId })
          .select("id")
          .single();
        if (error || !created) throw new Error("Översättningen kunde inte sparas.");
        results.push({ locale: target, id: created.id, title: values.title });
      }
    }

    const { logEditorialActivity } = await import("@/lib/editorial.server");
    await logEditorialActivity(context as never, {
      entity: "blog",
      entityId: source.id,
      action: "translate",
      summary: `Översatte inlägget till ${data.targets.join(", ")}`,
      meta: { targets: data.targets },
    });

    return { versions: results };
  });

/**
 * Inspiration till redaktören: förslag på ämnen att skriva om, med omvärldsbevakning
 * av skolfrågor och vad som händer i Marbella/Estepona med omnejd.
 */
export const suggestBlogTopics = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        locale: localeSchema.default("sv"),
        theme: z.string().trim().max(120).default(""),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const month = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Madrid", month: "long", year: "numeric" });
    const system =
      "Du är redaktionell rådgivare åt Swedish International School, en svensk skola i Estepona/Marbella på Costa del Sol. Du föreslår blogguppslag som är relevanta för svenska familjer i Spanien: skolfrågor, undervisning, idrott, flytt och vardagsliv, samt säsong och evenemang i Marbella/Estepona med omnejd. Hitta aldrig på exakta datum, siffror eller nyheter du inte är säker på – formulera i så fall uppslaget som något redaktionen behöver kontrollera. Svara ENDAST med giltig JSON.";
    const user = `${await schoolFactsBlock()}

Månad: ${month}. Språk för inläggen: ${data.locale}. ${data.theme ? `Tema: ${data.theme}.` : ""}

Svara med JSON: {"ideas": [{"title": string, "angle": string (1-2 meningar om vinkeln), "audience": string, "keywords": string[] (2-4), "needsCheck": string (tomt om inget behöver faktakollas)}]} med 6 förslag.`;

    const raw = await aiText(system, user);
    const json = raw.slice(raw.indexOf("{"), raw.lastIndexOf("}") + 1);
    let parsed: { ideas?: unknown };
    try {
      parsed = JSON.parse(json) as { ideas?: unknown };
    } catch {
      throw new Error("AI-svaret kunde inte tolkas. Försök igen.");
    }

    await logAdminAction(context as never, {
      action: "blog.ai_topics",
      entity: "blog_post",
      summary: "Hämtade inspirationsförslag till bloggen",
      meta: { locale: data.locale, theme: data.theme },
    });

    const list = Array.isArray(parsed.ideas) ? parsed.ideas : [];
    return {
      ideas: list.slice(0, 8).map((raw) => {
        const item = (raw ?? {}) as Record<string, unknown>;
        const str = (k: string) => (typeof item[k] === "string" ? (item[k] as string) : "");
        return {
          title: str("title"),
          angle: str("angle"),
          audience: str("audience"),
          keywords: Array.isArray(item["keywords"])
            ? (item["keywords"] as unknown[])
                .filter((k): k is string => typeof k === "string")
                .slice(0, 4)
            : [],
          needsCheck: str("needsCheck"),
        };
      }),
    };
  });

/* ------------------ Översättning med förhandsgranskning ------------------ */

const LANG_NAMES = { sv: "Swedish", en: "English", es: "Spanish (Spain)" } as const;

function parseAiJson(raw: string, message: string): Record<string, unknown> {
  const json = raw.slice(raw.indexOf("{"), raw.lastIndexOf("}") + 1);
  try {
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    throw new Error(message);
  }
}

/**
 * Släpper bara igenom förslag som är färdig text att klistra in.
 * AI:n svarar ibland med råd ("För att undvika upprepning, välj en annan …") –
 * sådant ska aldrig hamna i redigeringsfältet.
 */
function cleanSuggestion(raw: string): string {
  const value = raw.trim();
  if (!value) return "";
  const meta =
    /^(för att|du (bör|kan|behöver)|överväg|välj (en|ett)|byt (ut|till)|skriv (om|en|ett)|använd (en|ett|istället)|förkorta|korta ner|lägg till|ta bort|ersätt|föreslå|förslag:|tips:|to avoid|consider |rewrite |use a |shorten |para evitar|considera |reescrib|usa un)/i;
  if (meta.test(value)) return "";
  // Meningar som pratar om fältet i stället för att vara fältets text.
  if (/\b(rubriken|titeln|texten|fältet|inlägget|formulering(en|ar)?)\b.*\b(bör|ska|kan|behöver)\b/i.test(value))
    return "";
  return value;
}

/**
 * Översätter innehållet från en annan språkversion till det språk redaktören står i,
 * utan att skriva något i databasen. Redaktören godkänner fält för fält i redigeraren.
 */
export const previewBlogTranslation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ sourceId: z.string().uuid(), target: localeSchema }).parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const { data: source } = await context.supabase
      .from("blog_posts")
      .select("*")
      .eq("id", data.sourceId)
      .maybeSingle();
    if (!source) throw new Error("Källinlägget hittades inte.");

    const system = `You translate editorial content for Swedish International School in Estepona, Spain, into ${LANG_NAMES[data.target]}. Translate faithfully, keep the markdown structure, keep proper nouns, never invent facts. Reply ONLY with valid JSON.`;
    const user = `Translate this blog post into ${LANG_NAMES[data.target]}. Keep any {{LUCKA: ...}} markers untranslated and in place.

JSON in:
${JSON.stringify({
  title: source.title,
  excerpt: source.excerpt,
  content: source.content,
  seoTitle: source.seo_title,
  seoDescription: source.seo_description,
  category: source.category,
  tags: source.tags ?? [],
  coverImageAlt: source.cover_image_alt,
}).slice(0, 40000)}

Reply with JSON: {"title": string, "excerpt": string, "content": string (markdown), "seoTitle": string (max ${SEO_TITLE_MAX} chars), "seoDescription": string (${SEO_DESC_MIN}-${SEO_DESC_MAX} chars), "category": string, "tags": string[], "coverImageAlt": string, "slug": string (url friendly, lowercase, ${data.target})}`;

    const parsed = parseAiJson(
      await aiText(system, user),
      "Översättningen kunde inte tolkas. Försök igen.",
    );
    const str = (k: string) => (typeof parsed[k] === "string" ? (parsed[k] as string) : "");

    await logAdminAction(context as never, {
      action: "blog.translate_preview",
      entity: "blog_post",
      entityId: data.sourceId,
      summary: `Förhandsgranskade översättning till ${data.target}`,
      meta: { target: data.target },
    });

    return {
      sourceLocale: source.locale,
      fields: {
        title: str("title"),
        slug: slugify(str("slug") || str("title")),
        excerpt: str("excerpt"),
        content: str("content"),
        seoTitle: normalizeSeoTitle(str("seoTitle")),
        seoDescription: normalizeSeoDescription(str("seoDescription"), str("excerpt")),
        category: str("category") || (source.category ?? ""),
        coverImageAlt: str("coverImageAlt") || (source.cover_image_alt ?? ""),
        tags: Array.isArray(parsed["tags"])
          ? (parsed["tags"] as unknown[]).filter((t): t is string => typeof t === "string").slice(0, 8)
          : ((source.tags ?? []) as string[]),
      },
    };
  });

/* --------------------- Versioner för alla språkversioner ------------------ */

/** Versionshistorik för hela språkgruppen, så man kan gå tillbaka i tiden på alla språk. */
export const listGroupRevisions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { isAdmin } = await assertBlogAccess(context as never);
    const { data: row } = await context.supabase
      .from("blog_posts")
      .select("id, locale, translation_group_id")
      .eq("id", data.id)
      .maybeSingle();
    if (!row) throw new Error("Inlägget hittades inte.");

    let posts = [{ id: row.id, locale: row.locale, title: "" }];
    if (row.translation_group_id) {
      const { data: rows } = await context.supabase
        .from("blog_posts")
        .select("id, locale, title")
        .eq("translation_group_id", row.translation_group_id)
        .is("deleted_at", null);
      if (rows && rows.length > 0) posts = rows;
    }
    const ids = posts.map((p) => p.id);
    const { data: revisions } = await context.supabase
      .from("blog_post_revisions")
      .select("id, post_id, note, created_at, created_by_email, snapshot")
      .in("post_id", ids)
      .order("created_at", { ascending: false })
      .limit(120);

    const localeById = new Map(posts.map((p) => [p.id, p.locale]));
    return {
      isAdmin,
      revisions: (revisions ?? []).map((r) => ({
        id: r.id,
        postId: r.post_id,
        locale: localeById.get(r.post_id) ?? "",
        note: r.note ?? "",
        createdAt: r.created_at,
        createdByEmail: r.created_by_email ?? "",
        snapshot: r.snapshot,
      })),
    };
  });

/* ----------------------------- Granska allt ------------------------------ */

export type ReviewIssue = {
  id: string;
  locale: string;
  postId: string | null;
  field: string;
  fieldLabel: string;
  severity: "must" | "should" | "tip";
  title: string;
  detail: string;
  fixable: boolean;
  /** Konkret förslag på ny text för fältet, framtaget redan vid granskningen. */
  suggestion?: string;
};

const FIELD_LABELS: Record<string, string> = {
  title: "Titel",
  slug: "Webbadress",
  excerpt: "Ingress",
  content: "Brödtext",
  seoTitle: "SEO-titel",
  seoDescription: "Metabeskrivning",
  coverImageUrl: "Omslagsbild",
  coverImageAlt: "Alt-text omslag",
  tags: "Etiketter",
  category: "Kategori",
  general: "Allmänt",
};

const reviewRowSchema = z.object({
  postId: z.string().uuid().nullable().default(null),
  locale: localeSchema,
  title: z.string().default(""),
  slug: z.string().default(""),
  excerpt: z.string().default(""),
  content: z.string().default(""),
  seoTitle: z.string().default(""),
  seoDescription: z.string().default(""),
  coverImageUrl: z.string().default(""),
  coverImageAlt: z.string().default(""),
  category: z.string().default(""),
  tags: z.array(z.string()).default([]),
});

type ReviewRow = z.infer<typeof reviewRowSchema>;

function ruleIssues(row: ReviewRow): ReviewIssue[] {
  const out: ReviewIssue[] = [];
  const add = (
    field: string,
    severity: ReviewIssue["severity"],
    title: string,
    detail: string,
    fixable = true,
    suggestion?: string,
  ) =>
    out.push({
      id: `${row.locale}:${field}:${title}`,
      locale: row.locale,
      postId: row.postId,
      field,
      fieldLabel: FIELD_LABELS[field] ?? field,
      severity,
      title,
      detail,
      fixable,
      ...(suggestion ? { suggestion } : {}),
    });

  if (!row.title.trim()) add("title", "must", "Titeln saknas", "Inlägget har ingen rubrik.");
  else if (row.title.trim().length > 70)
    add("title", "should", "Titeln är lång", `${row.title.trim().length} tecken – korta ner till under 70.`);
  if (!row.excerpt.trim())
    add("excerpt", "must", "Ingressen saknas", "Ingressen visas i listan och i delningar.");
  if (row.content.trim().length < 300)
    add("content", "should", "Texten är kort", "Brödtexten är kortare än 300 tecken.");
  if (!row.seoTitle.trim())
    add(
      "seoTitle",
      "must",
      "SEO-titeln saknas",
      "Utan SEO-titel används rubriken rakt av.",
      true,
      row.title.trim().slice(0, SEO_TITLE_MAX),
    );
  else if (row.seoTitle.length > SEO_TITLE_MAX)
    add("seoTitle", "should", "SEO-titeln är för lång", `Max ${SEO_TITLE_MAX} tecken, nu ${row.seoTitle.length}.`);
  if (!row.seoDescription.trim())
    add(
      "seoDescription",
      "must",
      "Metabeskrivningen saknas",
      "Google visar då ett slumpmässigt utdrag.",
      true,
      row.excerpt.trim().slice(0, SEO_DESC_MAX),
    );
  else if (row.seoDescription.length > SEO_DESC_MAX)
    add("seoDescription", "should", "Metabeskrivningen är för lång", `Max ${SEO_DESC_MAX} tecken, nu ${row.seoDescription.length}.`);
  else if (row.seoDescription.length < SEO_DESC_MIN)
    add("seoDescription", "tip", "Metabeskrivningen är kort", `Minst ${SEO_DESC_MIN} tecken rekommenderas.`);
  if (!row.coverImageUrl.trim())
    add("coverImageUrl", "should", "Omslagsbild saknas", "Inlägget syns bättre i listor och delningar med bild.", false);
  else if (!row.coverImageAlt.trim())
    add("coverImageAlt", "must", "Alt-text saknas på omslagsbilden", "Alt-text krävs för tillgänglighet och SEO.");
  if (!row.slug.trim()) add("slug", "must", "Webbadressen saknas", "Inlägget behöver en adress.");
  else if (row.title.trim() && !row.slug.startsWith(slugify(row.title).slice(0, 12)))
    add("slug", "tip", "Adressen matchar inte rubriken", `Adress: ${row.slug}. Ändra bara om inlägget aldrig publicerats.`);
  if (row.tags.length === 0)
    add("tags", "tip", "Inga etiketter", "Etiketter gör det lättare att hitta inlägget.");
  if (!row.category.trim()) add("category", "tip", "Kategori saknas", "Välj en kategori för inlägget.");

  const gaps = [
    ...findGaps(row.title, "title", "Titel"),
    ...findGaps(row.excerpt, "excerpt", "Ingress"),
    ...findGaps(row.content, "content", "Brödtext"),
  ];
  for (const gap of gaps)
    add(gap.field, "must", "Lucka kvar i texten", `AI:n lämnade kvar ${gap.text} i ${gap.label.toLowerCase()}.`);

  return out;
}

/** Granskar hela inlägget och alla språkversioner: regler + AI-genomgång. */
export const reviewBlogGroup = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid().nullable().default(null),
        /** Aktivt språk som det ser ut i redigeraren just nu (kan vara osparat). */
        current: reviewRowSchema,
        /** Signatur av innehållet som granskas, så vi vet om något ändrats sedan sist. */
        contentHash: z.string().max(120).nullable().default(null),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const rows: ReviewRow[] = [data.current];

    if (data.id) {
      const { data: post } = await context.supabase
        .from("blog_posts")
        .select("translation_group_id")
        .eq("id", data.id)
        .maybeSingle();
      if (post?.translation_group_id) {
        const { data: siblings } = await context.supabase
          .from("blog_posts")
          .select("*")
          .eq("translation_group_id", post.translation_group_id)
          .is("deleted_at", null);
        for (const row of siblings ?? []) {
          if (row.id === data.id) continue;
          rows.push({
            postId: row.id,
            locale: row.locale as "sv" | "en" | "es",
            title: row.title ?? "",
            slug: row.slug ?? "",
            excerpt: row.excerpt ?? "",
            content: row.content ?? "",
            seoTitle: row.seo_title ?? "",
            seoDescription: row.seo_description ?? "",
            coverImageUrl: row.cover_image_url ?? "",
            coverImageAlt: row.cover_image_alt ?? "",
            category: row.category ?? "",
            tags: (row.tags ?? []) as string[],
          });
        }
      }
    }

    const issues: ReviewIssue[] = rows.flatMap(ruleIssues);
    const missing = (["sv", "en", "es"] as const).filter((l) => !rows.some((r) => r.locale === l));
    for (const locale of missing)
      issues.push({
        id: `${locale}:general:missing`,
        locale,
        postId: null,
        field: "general",
        fieldLabel: "Allmänt",
        severity: "should",
        title: "Språkversionen saknas",
        detail: "Skapa versionen via språkraden överst och översätt från ett annat språk.",
        fixable: false,
      });

    // AI-genomgång: språk, ton och skillnader mellan språkversionerna.
    try {
      const system =
        "Du är redaktionell granskare för Swedish International School i Estepona. Du granskar ett blogginlägg och dess översättningar. Peka bara ut konkreta problem: språkfel, otydliga meningar, felaktig ton, innehåll som finns på ett språk men saknas på ett annat, upprepningar och rubriker som inte hänger ihop. Hitta aldrig på fakta. Svara ENDAST med giltig JSON på svenska.";
      const user = `Språkversioner:
${JSON.stringify(
  rows.map((r) => ({
    locale: r.locale,
    title: r.title,
    excerpt: r.excerpt,
    content: r.content.slice(0, 12000),
    seoTitle: r.seoTitle,
    seoDescription: r.seoDescription,
  })),
).slice(0, 45000)}

Svara med JSON: {"issues": [{"locale": "sv"|"en"|"es", "field": "title"|"excerpt"|"content"|"seoTitle"|"seoDescription"|"general", "severity": "must"|"should"|"tip", "title": string (kort), "detail": string (1-2 meningar, konkret), "suggestion": string}]} med max 12 poster.

VIKTIGT om "suggestion": det ska vara den FÄRDIGA nya texten som ska stå i fältet, skriven på fältets eget språk och redo att klistras in rakt av."För att undvika upprepning, välj en annan formulering", "Skriv om ...", "Använd en kortare titel". Kan du inte skriva den färdiga texten – utelämna "suggestion" helt. För field "general" utelämnas "suggestion" alltid.`;

      const parsed = parseAiJson(await aiText(system, user), "AI-granskningen kunde inte tolkas.");
      const list = Array.isArray(parsed["issues"]) ? (parsed["issues"] as unknown[]) : [];
      for (const raw of list.slice(0, 12)) {
        const item = (raw ?? {}) as Record<string, unknown>;
        const str = (k: string) => (typeof item[k] === "string" ? (item[k] as string) : "");
        const locale = ["sv", "en", "es"].includes(str("locale")) ? str("locale") : rows[0]!.locale;
        const field = FIELD_LABELS[str("field")] ? str("field") : "general";
        const severity = (["must", "should", "tip"] as const).includes(
          str("severity") as "must",
        )
          ? (str("severity") as ReviewIssue["severity"])
          : "should";
        if (!str("title")) continue;
        const suggestion = field === "general" ? "" : cleanSuggestion(str("suggestion"));
        issues.push({
          id: `ai:${locale}:${field}:${str("title")}`,
          locale,
          postId: rows.find((r) => r.locale === locale)?.postId ?? null,
          field,
          fieldLabel: FIELD_LABELS[field] ?? field,
          severity,
          title: str("title"),
          detail: str("detail"),
          fixable: field !== "general",
          ...(suggestion ? { suggestion } : {}),
        });
      }
    } catch {
      issues.push({
        id: "ai:general:failed",
        locale: rows[0]!.locale,
        postId: data.id,
        field: "general",
        fieldLabel: "Allmänt",
        severity: "tip",
        title: "AI-granskningen kunde inte köras",
        detail: "Regelkontrollerna visas ändå. Klicka på Granska igen för ett nytt försök.",
        fixable: false,
      });
    }

    const result = {
      checkedAt: new Date().toISOString(),
      contentHash: data.contentHash,
      issues,
    };

    if (data.id) {
      await context.supabase.from("editorial_ai_plans").upsert(
        {
          user_id: context.userId,
          entity: "blog",
          entity_key: `review:${data.id}`,
          plan: JSON.parse(JSON.stringify(result)) as Json,
          updated_at: result.checkedAt,
        },
        { onConflict: "user_id,entity,entity_key" },
      );
    }

    return result;
  });

/** Hämtar senaste sparade granskning, så samma lista visas igen. */
export const loadBlogReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const { data: row } = await context.supabase
      .from("editorial_ai_plans")
      .select("plan, updated_at")
      .eq("user_id", context.userId)
      .eq("entity", "blog")
      .eq("entity_key", `review:${data.id}`)
      .maybeSingle();
    if (!row)
      return {
        review: null as null | {
          checkedAt: string;
          contentHash?: string | null;
          issues: ReviewIssue[];
        },
      };
    return {
      review: row.plan as unknown as {
        checkedAt: string;
        contentHash?: string | null;
        issues: ReviewIssue[];
      },
    };
  });

/** Tar fram ett konkret förslag som åtgärdar en enskild anmärkning. */
export const fixBlogIssue = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        locale: localeSchema,
        field: z.enum([
          "title",
          "slug",
          "excerpt",
          "content",
          "seoTitle",
          "seoDescription",
          "coverImageAlt",
          "category",
        ]),
        issue: z.string().max(600),
        detail: z.string().max(1200).default(""),
        row: reviewRowSchema,
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const system = `Du är redaktör för Swedish International School i Estepona. Du åtgärdar EN sak i ett blogginlägg skrivet på ${LANG_NAMES[data.locale]}. Behåll språket, tonen och all fakta. Hitta aldrig på nya fakta, datum eller siffror. Behåll markdown-strukturen. Svara ENDAST med giltig JSON.`;
    const user = `Anmärkning: ${data.issue}. ${data.detail}

Fält som ska åtgärdas: ${FIELD_LABELS[data.field] ?? data.field} (${data.field}).

Inlägget:
${JSON.stringify({
  title: data.row.title,
  excerpt: data.row.excerpt,
  content: data.row.content.slice(0, 20000),
  seoTitle: data.row.seoTitle,
  seoDescription: data.row.seoDescription,
  coverImageAlt: data.row.coverImageAlt,
  category: data.row.category,
})}

Regler: ${SEO_RULES_PROMPT}

Svara med JSON: {"value": string (hela det nya fältvärdet), "note": string (kort förklaring på svenska)}`;

    const parsed = parseAiJson(await aiText(system, user), "AI-förslaget kunde inte tolkas.");
    let value = typeof parsed["value"] === "string" ? (parsed["value"] as string) : "";
    if (!value.trim()) throw new Error("AI:n lämnade ett tomt förslag. Försök igen.");
    if (data.field === "seoTitle") value = normalizeSeoTitle(value);
    if (data.field === "seoDescription")
      value = normalizeSeoDescription(value, data.row.excerpt);
    if (data.field === "slug") value = slugify(value);
    return {
      value,
      note: typeof parsed["note"] === "string" ? (parsed["note"] as string) : "",
    };
  });

/** Kopierar omslagsbildens utsnitt till alla språkversioner av samma inlägg. */
export const copyCoverFrameToTranslations = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        focalX: z.number().min(0).max(100),
        focalY: z.number().min(0).max(100),
        aspect: z.string().max(20),
        fit: z.enum(["cover", "contain"]),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertBlogAccess(context as never);
    const { data: source } = await context.supabase
      .from("blog_posts")
      .select("translation_group_id")
      .eq("id", data.id)
      .maybeSingle();
    if (!source?.translation_group_id) throw new Error("Inlägget saknar en kopplad språkgrupp.");

    const { error, count } = await context.supabase
      .from("blog_posts")
      .update(
        {
          cover_focal_x: Math.round(data.focalX),
          cover_focal_y: Math.round(data.focalY),
          cover_aspect: data.aspect,
          cover_fit: data.fit,
          updated_by: context.userId,
        },
        { count: "exact" },
      )
      .eq("translation_group_id", source.translation_group_id)
      .is("deleted_at", null);
    if (error) throw new Error("Utsnittet kunde inte kopieras till de andra språken.");
    return { updated: count ?? 0 };
  });
